import { Stack, Typography } from '@mui/material';
import { useSubmission, useUnderwritingQuestions, useUpdateUwQuestions } from 'api/submission';
import {
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES,
  FEIN_FIELD_CODE,
  FEIN_FIELD_FOR_MANAGEMENT_FINANCIALS,
  IS_PAYROLL_WORKSHEET_ENABLED,
  TAX_ID_TYPE_CHOICE_EMPTY_FIELD_CODE,
  TAX_ID_TYPE_CHOICE_FEIN_FIELD_CODE,
  TAX_ID_TYPE_FIELD_CODE,
} from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { QuestionEngine, useQuestionEngine } from 'components/lib/QuestionEngine';
import { handleShowCondition } from 'components/lib/QuestionEngine/QuestionEngine.utils';
import { computePayrollWorksheetTotals } from 'components/PayrollWorksheetContainer/helpers/computePayrollWorksheetTotals';
import {
  getPayrollWorksheetValidations,
  PAYROLL_WORKSHEET_CONTAINER_FIELD_CODES,
} from 'components/PayrollWorksheetContainer/helpers/payrollWorksheetValidations';
import PayrollWorksheetContainer, {
  PAYROLL_WORKSHEET_FIELD_CODES,
} from 'components/PayrollWorksheetContainer/PayrollWorksheetContainer';
import { StepFooterActions } from 'components/StepFooterActions';
import { isLowPayrollPerEmployeeTriggered } from 'helpers/isLowPayrollPerEmployeeTriggered';
import {
  fieldsContainAnyCode,
  formatThreeConfigVersion,
  hasWorksheetDataIn,
  isExpandedHotelPayrollWorksheet,
  isValuePresent,
} from 'helpers/utils';
import { isEmpty, isEqual, pick } from 'lodash-es';
import React, { type FC, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';
import type { QuoteFlowStep } from 'types/QuoteFlow';

const MANAGEMENT_FINANCIALS_CODE = 'management_&_financials';
const PAYROLL_WORKSHEET_INSERT_AFTER_FIELD_CODE = 'payrollW2';
const PAYROLL_WORKSHEET_START_QUESTION_CODE = 'isSeasonalBusiness';
const PAYROLL_WORKSHEET_READONLY_FIELD_CODES = new Set([
  'fullTimeEstimatedAnnualPayroll',
  'partTimeEstimatedAnnualPayroll',
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEstimatedAnnualPayroll,
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEstimatedAnnualPayroll,
]);

const EXPANDED_HOTEL_FIELD_CODE_SET = new Set<string>(
  Object.values(EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES),
);

const PAYROLL_WORKSHEET_END_QUESTION_CODES = new Set([
  'partTimeEstimatedAnnualPayroll',
  EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEstimatedAnnualPayroll,
]);

interface DynamicStepProps {
  step: QuoteFlowStep;
}

// eslint-disable-next-line complexity
const DynamicStep: FC<DynamicStepProps> = ({ step }) => {
  const { id } = useParams() as { id: string };
  const { data: submission, isLoading: isSubmissionLoading } = useSubmission(id);
  const { mutateAsync: saveUnderwritingQuestions } = useUpdateUwQuestions(id);
  const { data: questions, isLoading: isQuestionsLoading } = useUnderwritingQuestions(id, true);

  const isPageLoading = isSubmissionLoading || isQuestionsLoading;

  const fields = useMemo(
    () =>
      questions?.flattenQuestions.find((question) => question.code === step.key)?.nested_fields ||
      [],
    [step.key, questions],
  );

  const isManagementFinancials = step.key === MANAGEMENT_FINANCIALS_CODE;
  const isPayrollWorksheetApplicable = isManagementFinancials && IS_PAYROLL_WORKSHEET_ENABLED;

  const payrollWorksheetFields = useMemo(() => {
    if (!isPayrollWorksheetApplicable) return [];
    const nestedFields = fields;
    const startIdx = nestedFields.findIndex(
      (field: { code?: string }) => field?.code === PAYROLL_WORKSHEET_START_QUESTION_CODE,
    );
    let endIdx = -1;
    for (let i = nestedFields.length - 1; i >= 0; i--) {
      if (PAYROLL_WORKSHEET_END_QUESTION_CODES.has(nestedFields[i]?.code ?? '')) {
        endIdx = i;
        break;
      }
    }
    if (startIdx === -1 || endIdx === -1 || startIdx > endIdx) return [];
    return nestedFields.slice(startIdx, endIdx + 1);
  }, [fields, isPayrollWorksheetApplicable]);

  const { setFullscreenLoading } = useFullscreenLoader();

  const polTaxKeyword = submission?.data?.policy_data?.pol_tax_keyword as string | undefined;

  const isExpandedHotel = useMemo(
    (): boolean => isExpandedHotelPayrollWorksheet(polTaxKeyword, payrollWorksheetFields),
    [polTaxKeyword, payrollWorksheetFields],
  );

  const getAdditionalValidationFields = useMemo(() => {
    if (!isPayrollWorksheetApplicable || !payrollWorksheetFields.length) return undefined;
    return (values: Record<string, unknown>) =>
      payrollWorksheetFields.filter(
        (field: { code?: string; show_condition?: unknown }) =>
          field &&
          !PAYROLL_WORKSHEET_READONLY_FIELD_CODES.has(field.code ?? '') &&
          (!EXPANDED_HOTEL_FIELD_CODE_SET.has(field.code ?? '') || isExpandedHotel) &&
          handleShowCondition(field as Parameters<typeof handleShowCondition>[0], values),
      );
  }, [isPayrollWorksheetApplicable, payrollWorksheetFields, isExpandedHotel]);

  const customValidation = useMemo(
    () =>
      isPayrollWorksheetApplicable && payrollWorksheetFields.length > 0
        ? getPayrollWorksheetValidations(isExpandedHotel)
        : {},
    [isPayrollWorksheetApplicable, payrollWorksheetFields.length, isExpandedHotel],
  );

  const { validateFormik, updateState, ...questionEngineProps } = useQuestionEngine({
    fields,
    showLoader: isPageLoading,
    wrapperSx: { mb: 3 },
    getAdditionalValidationFields,
    customValidation,
  });
  const { state, setState, formik, ref: questionEngineRef } = questionEngineProps;

  const businessInfoQuestionFields = useMemo(() => {
    if (!isPayrollWorksheetApplicable) return fields;
    const isLowPayrollTriggered = state?.payrollWorksheet === 'Yes';
    if (!isLowPayrollTriggered) return fields;
    const excludeCodes = new Set(
      payrollWorksheetFields.map((field: { code?: string }) => field?.code).filter(Boolean),
    );
    EXPANDED_HOTEL_FIELD_CODE_SET.forEach((code) => excludeCodes.add(code));
    return fields.filter((field: { code?: string }) => !excludeCodes.has(field?.code ?? ''));
  }, [fields, isPayrollWorksheetApplicable, payrollWorksheetFields, state?.payrollWorksheet]);

  const characteristics = useMemo(
    () => ({ pol_tax_industry: submission?.data?.policy_data?.pol_tax_industry }),
    [submission?.data?.policy_data?.pol_tax_industry],
  );
  const submissionState = useMemo(() => {
    const uwQuestions = submission?.data?.uw_questions ?? {};
    return {
      effective_date: submission?.effective_date,
      ...(submission?.data?.policy_data ?? {}),
      ...uwQuestions,
      grossRevenueUser: uwQuestions.grossRevenueUser ?? uwQuestions.grossRevenue,
      productVersion: formatThreeConfigVersion(submission?.three_config_version),
    };
  }, [
    submission?.effective_date,
    submission?.three_config_version,
    submission?.data?.policy_data,
    submission?.data?.uw_questions,
  ]);

  const lastSyncedSubmissionRef = useRef<object | null>(null);
  const isInitialSyncRef = useRef(true);

  // When backend submission data changed, sync it to question engine state.
  // Only auto-open the worksheet (via hasWorksheetDataIn) on the INITIAL sync so that
  // a save that explicitly closes the worksheet is not immediately overridden by stale
  // numeric zeros the backend may return for cleared worksheet fields.
  useEffect(() => {
    if (!isEmpty(submission?.data) && lastSyncedSubmissionRef.current !== submission?.data) {
      lastSyncedSubmissionRef.current = submission?.data;

      const allPayrollFieldCodes = [
        ...Object.values(PAYROLL_WORKSHEET_FIELD_CODES),
        ...Object.values(EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES),
      ];

      const shouldAutoOpenWorksheet =
        isInitialSyncRef.current &&
        isPayrollWorksheetApplicable &&
        hasWorksheetDataIn(submissionState, allPayrollFieldCodes);

      isInitialSyncRef.current = false;

      const nextState = shouldAutoOpenWorksheet
        ? { ...submissionState, payrollWorksheet: 'Yes' as const }
        : { ...submissionState };
      updateState(nextState);
    }
  }, [submission?.data, submissionState, isPayrollWorksheetApplicable]);

  const areStatesEqual = () => {
    const renderedQuestionCodes =
      questionEngineRef?.current?.renderedQuestions()?.map((question) => question.code) || [];

    return isEqual(
      pick(state, renderedQuestionCodes),
      pick(submissionState, renderedQuestionCodes),
    );
  };

  const isLowPayrollTriggered = state?.payrollWorksheet === 'Yes';

  const scrollToFirstError = (): void => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const errorElement = document.querySelector('.Mui-error, [data-error]');
        const target = errorElement?.parentElement ?? errorElement;
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  };

  // eslint-disable-next-line complexity
  const handleNext = async () => {
    const isValid = await validateFormik();

    if (!isValid) {
      scrollToFirstError();
      return false;
    }
    // Payroll worksheet trigger: when low payroll per employee
    // show container and stay on page
    if (
      isPayrollWorksheetApplicable &&
      state &&
      !state?.payrollWorksheet &&
      isLowPayrollPerEmployeeTriggered(state, characteristics) &&
      fieldsContainAnyCode(fields, Object.values(PAYROLL_WORKSHEET_FIELD_CODES))
    ) {
      setState?.((prev) => ({ ...prev, payrollWorksheet: 'Yes' }));
      formik.setFieldValue('payrollWorksheet', 'Yes');
      return false;
    }

    let payrollStateOverride: Record<string, string> = {};
    if (isPayrollWorksheetApplicable && state?.payrollWorksheet === 'Yes') {
      const { totalEmployeeCount, totalEstimatedPayroll, hasValidBreakdown, hasWorksheetData } =
        computePayrollWorksheetTotals(state as Record<string, unknown>, isExpandedHotel);

      if (hasWorksheetData && hasValidBreakdown && totalEmployeeCount <= 1) {
        const worksheetFieldsReset = Object.fromEntries(
          PAYROLL_WORKSHEET_CONTAINER_FIELD_CODES.map((code) => [code, '']),
        );
        payrollStateOverride = {
          ...worksheetFieldsReset,
          numEmployees: String(totalEmployeeCount),
          payrollW2: String(totalEstimatedPayroll),
          payrollWorksheet: '',
        };
        setState?.((prev) => ({ ...prev, ...payrollStateOverride }));
        formik.setValues((prev) => ({ ...prev, ...payrollStateOverride }));
      } else if (hasValidBreakdown && hasWorksheetData) {
        payrollStateOverride = {
          numEmployees: String(totalEmployeeCount),
          payrollW2: String(totalEstimatedPayroll),
        };
        setState?.((prev) => ({ ...prev, ...payrollStateOverride }));
        formik.setFieldValue('numEmployees', String(totalEmployeeCount));
        formik.setFieldValue('payrollW2', String(totalEstimatedPayroll));
      }

      if (payrollStateOverride.numEmployees) {
        const stateBeforeOverride = state as Record<string, unknown>;
        const stateAfterOverride = { ...stateBeforeOverride, ...payrollStateOverride };
        const wasFeinVisible = handleShowCondition(
          FEIN_FIELD_FOR_MANAGEMENT_FINANCIALS,
          stateBeforeOverride,
        );
        const isFeinVisibleAfter = handleShowCondition(
          FEIN_FIELD_FOR_MANAGEMENT_FINANCIALS,
          stateAfterOverride,
        );

        if (wasFeinVisible && !isFeinVisibleAfter) {
          payrollStateOverride = { ...payrollStateOverride, fein: '' };
          setState?.((prev) => ({ ...prev, fein: '' }));
          formik.setFieldValue('fein', '');
        }

        if (!wasFeinVisible && isFeinVisibleAfter && !isValuePresent(stateBeforeOverride.fein)) {
          // If FEIN becomes visible and has no value, set an error to trigger validation and scroll to it
          await validateFormik();
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const element =
                document.querySelector('.Mui-error, [data-error]') ??
                document.getElementById('fein');
              (element?.parentElement ?? element)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            });
          });
          return false;
        }
      }
    }

    /**
     * if the backend submission state is same as the question engine state,
     * let changing the step without saving
     */
    if (isEmpty(payrollStateOverride) && areStatesEqual()) {
      return true;
    }

    try {
      setFullscreenLoading(true, 'Saving the answers');

      const codesToSave = questions?.allQuestionCodes || [];
      const mergedState = isEmpty(payrollStateOverride)
        ? state
        : { ...state, ...payrollStateOverride };
      const payrollWorksheetValue =
        payrollStateOverride.payrollWorksheet !== undefined
          ? payrollStateOverride.payrollWorksheet
          : (state?.payrollWorksheet as string | undefined);

      const updateTaxIDValues: Record<string, string> = {};
      if (isPayrollWorksheetApplicable) {
        const feinIsRendered = handleShowCondition(
          FEIN_FIELD_FOR_MANAGEMENT_FINANCIALS,
          mergedState as Record<string, unknown>,
        );
        if (!feinIsRendered) {
          updateTaxIDValues[FEIN_FIELD_CODE] = '';
          updateTaxIDValues[TAX_ID_TYPE_FIELD_CODE] = TAX_ID_TYPE_CHOICE_EMPTY_FIELD_CODE;
        } else {
          updateTaxIDValues[TAX_ID_TYPE_FIELD_CODE] = TAX_ID_TYPE_CHOICE_FEIN_FIELD_CODE;
        }
      }

      const body = {
        ...pick(mergedState, codesToSave),
        ...updateTaxIDValues,
        ...(isPayrollWorksheetApplicable && payrollWorksheetValue !== undefined
          ? { payrollWorksheet: payrollWorksheetValue }
          : {}),
      };
      await saveUnderwritingQuestions({ id, body });

      return true;
    } catch (_error) {
      displayBackendErrorMessage(_error);
      return false;
    } finally {
      setFullscreenLoading(false);
    }
  };

  const handlePrevious = async () => {
    /**
     * if the backend submission state isn`t same as the question engine state,
     * update state with the backend submission state
     */
    if (!areStatesEqual()) {
      updateState(submissionState);
    }

    return true;
  };

  return (
    <Stack sx={{ flex: 1 }}>
      {!isPageLoading && (
        <Typography variant="h1" sx={{ mb: 3 }}>
          {step.label}
        </Typography>
      )}

      <QuestionEngine
        {...questionEngineProps}
        fields={
          isPayrollWorksheetApplicable ? businessInfoQuestionFields : questionEngineProps.fields
        }
        insertAfterField={
          isPayrollWorksheetApplicable && isLowPayrollTriggered
            ? {
                code: PAYROLL_WORKSHEET_INSERT_AFTER_FIELD_CODE,
                node: (
                  <PayrollWorksheetContainer
                    state={state ?? {}}
                    setState={
                      setState as React.Dispatch<React.SetStateAction<Record<string, unknown>>>
                    }
                    formik={formik}
                    payrollWorksheetFields={payrollWorksheetFields}
                    readonlyFieldCodes={Array.from(PAYROLL_WORKSHEET_READONLY_FIELD_CODES)}
                    isReadOnly={false}
                    isExpandedHotel={isExpandedHotel}
                  />
                ),
              }
            : undefined
        }
        readonlyFieldCodes={
          isPayrollWorksheetApplicable && isLowPayrollTriggered
            ? ['payrollW2', 'numEmployees']
            : undefined
        }
      />

      {!isPageLoading && (
        <StepFooterActions
          onNext={handleNext}
          onPrevious={handlePrevious}
          disableActions={isPageLoading}
        />
      )}
    </Stack>
  );
};

export default DynamicStep;
